import { IslandApiResponse } from 'state/data/slice'
import { ExplorerDataType, getExplorerLink } from 'utils/getExplorerLink'

import {
  AprCalculationParams,
  AprDisplayConfig,
  AprDisplayData,
  AprDisplayParams,
  DepositOption,
  ExternalLink,
  ExternalLinksParams,
  PoolConfig,
  PoolData,
  PoolMetrics,
  StakingData,
  StakingDataParams,
} from '../types'

export class IslandPoolConfig implements PoolConfig {
  readonly type = 'Island' as const
  readonly provider = 'kodiak' as const
  readonly supportsZapping = true
  readonly supportsAutoCompound = true
  readonly supportsFarms = true
  readonly supportsVaults = true

  getPoolData(poolApiResponse: IslandApiResponse): PoolData {
    return {
      lowerTick: poolApiResponse?.lowerTick,
      upperTick: poolApiResponse?.upperTick,
      currentTick: poolApiResponse?.currentTick,
      feeTier: poolApiResponse?.feeTier,
      poolAddress: poolApiResponse?.poolAddress ?? '',
      lpTokenAddress: poolApiResponse?.tokenLp?.id ?? '',
    }
  }

  getStakingData(params: StakingDataParams): StakingData {
    const {
      poolApiResponse,
      kodiakFarmAddress,
      rewardVaultAddress,
      isWhitelistedForRewardVault,
      migrationKodiakFarmAddress,
    } = params

    const isValidKodiakFarm = !!kodiakFarmAddress

    let stakingType: 'kodiakFarm' | 'rewardVault' | 'none' = 'none'

    if (isWhitelistedForRewardVault && rewardVaultAddress) {
      stakingType = 'rewardVault'
    } else if (kodiakFarmAddress && !rewardVaultAddress) {
      stakingType = 'kodiakFarm'
    }

    const baultAddress = this.getBaultAddress(poolApiResponse, stakingType)

    return {
      kodiakFarmAddress: isValidKodiakFarm ? kodiakFarmAddress : undefined,
      rewardVaultAddress: isWhitelistedForRewardVault ? rewardVaultAddress : undefined,
      migrationKodiakFarmAddress,
      baultAddress,
      baultFarmAddress: '',
      stakingType,
    }
  }

  getPoolMetrics(poolApiResponse: any, pool?: any): PoolMetrics {
    return {
      tvl: poolApiResponse?.tvl ?? pool?.totalValueLockedUSD ?? 0,
      farmTvl: poolApiResponse?.farmTvl,
      baseApr: poolApiResponse?.apr ?? 0,
      farmApr: !poolApiResponse?.isRewardVault ? poolApiResponse?.farm?.apr : undefined,
      rewardVaultRewardApr: poolApiResponse?.isRewardVault ? poolApiResponse?.farm?.apr : undefined,
      weeklyFees: poolApiResponse?.weeklyFeesEarnedUSD ?? 0,
    }
  }

  getDisplayApr({ farmApr, isLoadingFarm, metrics, aprDisplay }: AprCalculationParams): AprDisplayData | null {
    if (!aprDisplay.showKodiakFarmApr && !aprDisplay.showRewardVaultRewardApr) {
      return null
    }

    if (aprDisplay.showKodiakFarmApr && farmApr !== null) {
      return {
        apr: farmApr,
        label: aprDisplay.farmLabel,
        isLoading: isLoadingFarm,
      }
    }

    if (aprDisplay.showRewardVaultRewardApr && metrics.rewardVaultRewardApr !== undefined) {
      return {
        apr: metrics.rewardVaultRewardApr,
        label: aprDisplay.farmLabel,
        isLoading: false,
      }
    }

    return null
  }

  getExternalLinks({
    chainId,
    poolApiResponse,
    kodiakFarmAddress,
    rewardVaultAddress,
    baultAddress,
  }: ExternalLinksParams): ExternalLink[] {
    const links: ExternalLink[] = []

    if (poolApiResponse?.id) {
      links.push({
        label: 'Island Contract',
        url: getExplorerLink(chainId, poolApiResponse.id, ExplorerDataType.ADDRESS),
        copyText: poolApiResponse.id,
      })
    }

    if (poolApiResponse?.pool?.id) {
      links.push({
        label: 'Pool Contract',
        url: getExplorerLink(chainId, poolApiResponse?.pool.id, ExplorerDataType.ADDRESS),
        copyText: poolApiResponse.pool.id,
      })
    }

    if (kodiakFarmAddress) {
      links.push({
        label: 'Farm Contract',
        url: getExplorerLink(chainId, kodiakFarmAddress, ExplorerDataType.ADDRESS),
        copyText: poolApiResponse?.id,
      })
    }

    if (rewardVaultAddress) {
      links.push(
        {
          label: 'Reward Vault Contract',
          url: getExplorerLink(chainId, rewardVaultAddress, ExplorerDataType.ADDRESS),
          copyText: rewardVaultAddress,
        },
        {
          label: 'Berachain Hub',
          url: `https://hub.berachain.com/vaults/${rewardVaultAddress}`,
        }
      )
    }

    if (baultAddress) {
      links.push({
        label: 'Bault Contract',
        url: getExplorerLink(chainId, baultAddress, ExplorerDataType.ADDRESS),
        copyText: baultAddress,
      })
    }

    return links
  }

  getAprDisplay({
    isRewardVaultActive,
    isKodiakFarmActive,
    rewardVaultRewardSymbol,
  }: AprDisplayParams): AprDisplayConfig {
    return {
      showBaseApr: true,
      showKodiakFarmApr: isKodiakFarmActive && !isRewardVaultActive,
      showRewardVaultRewardApr: isRewardVaultActive,
      farmLabel: isRewardVaultActive ? `${rewardVaultRewardSymbol || 'Reward'} APR` : 'Farm APR',
    }
  }

  getDepositOptions(): DepositOption[] {
    return [
      { value: 'depositOnly', label: 'Deposit Only', disabled: false },
      { value: 'withStaking', label: 'With Staking', disabled: false },
    ]
  }

  private getBaultAddress(poolApiResponse: any, stakingType: StakingData['stakingType']): string | undefined {
    if (stakingType !== 'rewardVault') return undefined
    if (!poolApiResponse?.isRewardVault) return undefined
    if (this.type !== 'Island' && this.type !== 'Charm') return undefined

    return poolApiResponse?.baults?.[poolApiResponse.baults.length - 1]?.id
  }
}
